import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AcademicCapIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../contexts/AuthContext';
import { useClubSettings } from '../contexts/ClubSettingsContext';
import { helpModules } from '../help/modules';
import { canSeeHelp } from '../help/audience';
import { isFamilyUser } from '../types/auth';
import { HelpModule } from '../help/types';
import HelpModuleBody from '../components/help/HelpModuleBody';
import LearningModeSwitch from '../components/help/LearningModeSwitch';

const matches = (module: HelpModule, query: string) => {
  const haystack = [
    module.title,
    module.summary,
    ...module.steps,
    ...(module.tips ?? []),
    ...(module.items ?? []).flatMap((i) => [i.label, i.text]),
  ]
    .join(' ')
    .toLocaleLowerCase('hu');
  return haystack.includes(query.toLocaleLowerCase('hu'));
};

/** Minden funkció leírása egy helyen, a felhasználó jogosultságaira szűrve. */
const Help: React.FC = () => {
  usePageTitle('Súgó');
  const { user } = useAuth();
  const { settings } = useClubSettings();
  const location = useLocation();
  const [query, setQuery] = useState('');

  const visible = useMemo(() => {
    const list = helpModules.filter((m) => canSeeHelp(user, m, settings));
    if (!isFamilyUser(user)) return list;
    // A szülőnek a fülsor sorrendjében: Kezdőlap, Program, Hírek, Én, aztán a többi
    const order = ['family-home', 'family-program', 'board', 'family-me'];
    const rank = (id: string) => (order.includes(id) ? order.indexOf(id) : order.length);
    return [...list].sort((a, b) => rank(a.id) - rank(b.id));
  }, [user, settings]);
  const shown = query.trim() ? visible.filter((m) => matches(m, query.trim())) : visible;

  // A tanuló mód sávjából érkező /help#modul link a modulhoz görget
  useEffect(() => {
    const id = location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }, [location.hash]);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Súgó</h1>
        <p className="text-sm text-gray-500">Mire való az egyes modul, és hogyan használd.</p>
      </div>

      {/* A családi nézetben nincs tanuló mód: ott a felület magától érthető */}
      {!isFamilyUser(user) && (
      <div className="mb-6 flex flex-col gap-3 rounded-xl border border-primary-200 bg-primary-50/70 p-4 sm:flex-row sm:items-center">
        <AcademicCapIcon className="hidden h-8 w-8 shrink-0 text-primary-600 sm:block" />
        <div className="flex-1 text-sm text-gray-700">
          <p className="font-semibold text-gray-900">Tanuló mód</p>
          <p>
            Bekapcsolva minden oldal tetején megjelenik egy rövid útmutató, a fontosabb gombok mellett pedig egy ?
            jel, ami elmagyarázza őket.
          </p>
        </div>
        <LearningModeSwitch />
      </div>
      )}

      <div className="relative mb-4">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Keresés a súgóban, pl. tagdíj, felállás, sportorvosi..."
          aria-label="Keresés a súgóban"
          className="block w-full rounded-lg border-gray-300 pl-10 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
        />
      </div>

      {!query && (
        <nav aria-label="Modulok" className="mb-6 flex flex-wrap gap-2">
          {visible.map((m) => (
            <a
              key={m.id}
              href={`#${m.id}`}
              className="rounded-full bg-white px-3 py-1 text-sm text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50"
            >
              {m.title}
            </a>
          ))}
        </nav>
      )}

      {shown.length === 0 ? (
        <p className="rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow-sm">Nincs találat.</p>
      ) : (
        <div className="space-y-4">
          {shown.map((m) => (
            <article key={m.id} id={m.id} className="scroll-mt-24 rounded-xl bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-3 flex items-baseline justify-between gap-3">
                <h2 className="text-lg font-semibold text-gray-900">{m.title}</h2>
                <Link to={m.paths[0]} className="shrink-0 text-sm font-medium text-primary-700 hover:text-primary-800">
                  Megnyitás →
                </Link>
              </div>
              <HelpModuleBody module={m} />
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default Help;
